import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { isKeyboardLikelyOpen } from '../components/BottomTabBar';

/**
 * The part of the screen the member can actually see, for full-screen chat
 * layouts that must sit between the top chrome and the keyboard (owner's
 * iOS screenshot, 2026-10-23).
 *
 * Why this exists: TradeMessages pinned itself between the navbar and the
 * bottom tab bar using layout-viewport units. iOS does not shrink the layout
 * viewport for the keyboard; it shrinks the VISUAL viewport and scrolls the
 * page to reveal the focused field. So the chat (a) kept a 65px+inset band
 * reserved for a tab bar that hides while typing, leaving an empty gap above
 * the keyboard, and (b) got shoved up under the status bar by iOS's scroll.
 * Sizing the frame from window.visualViewport fixes both.
 *
 * The hook reports only the visible area (offsetTop, height). Clearing the
 * top chrome is done in CSS against the live --navbar-height variable
 * (TradeMessages), because the navbar re-measures itself with a
 * ResizeObserver (logo load, banners) without any window or viewport event
 * that would make a JS copy of it current.
 *
 * Keyboard detection is the tab bar's own rule (isKeyboardLikelyOpen), so
 * "keyboard open" means the same thing everywhere. Re-measures on the same
 * events the tab bar does, including resume, for the same
 * stale-after-background reason.
 */

export default function useVisibleViewport() {
  const [state, setState] = useState(() => ({
    offsetTop: 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    keyboardOpen: false,
    shrunk: false,
  }));

  useEffect(() => {
    const vv = window.visualViewport;
    const isNative = Capacitor.isNativePlatform();
    // `shrunk`: the visible area is well below its normal height, i.e. a
    // keyboard is taking the bottom of the screen. Needed on Android, where
    // the page itself is resized for the keyboard (window and visual
    // viewport shrink together, so isKeyboardLikelyOpen's gap test can't
    // fire) and where hiding the keyboard with its own arrow keeps the field
    // focused, so focus alone can't say whether the keyboard is up. The
    // normal height is the tallest seen at this width; a width change
    // (rotation) starts over.
    let baseline = { width: 0, height: 0 };
    const measure = () => {
      const height = Math.max(0, vv ? vv.height : window.innerHeight);
      const offsetTop = Math.max(0, vv ? vv.offsetTop : 0);
      const width = window.innerWidth;
      if (width !== baseline.width) baseline = { width, height: 0 };
      if (height > baseline.height) baseline.height = height;
      const shrunk = height < baseline.height - 150;
      const keyboardOpen = vv ? isKeyboardLikelyOpen({
        innerHeight: window.innerHeight,
        viewportHeight: vv.height,
        viewportScale: vv.scale,
        activeElement: document.activeElement,
      }) : false;
      setState((prev) => (
        prev.offsetTop === offsetTop && prev.height === height && prev.keyboardOpen === keyboardOpen && prev.shrunk === shrunk
          ? prev
          : { offsetTop, height, keyboardOpen, shrunk }
      ));
    };
    let soonTimer = null;
    const measureSoon = () => { clearTimeout(soonTimer); soonTimer = setTimeout(measure, 0); };
    if (vv) {
      vv.addEventListener('resize', measure);
      vv.addEventListener('scroll', measure);
    }
    window.addEventListener('resize', measure);
    document.addEventListener('visibilitychange', measure);
    document.addEventListener('focusin', measure);
    document.addEventListener('focusout', measureSoon);
    let resumeHandle = null;
    let disposed = false;
    if (isNative) {
      CapacitorApp.addListener('resume', measure).then((h) => {
        if (disposed) h.remove(); else resumeHandle = h;
      }).catch(() => {});
    }
    measure();
    return () => {
      disposed = true;
      clearTimeout(soonTimer);
      if (vv) {
        vv.removeEventListener('resize', measure);
        vv.removeEventListener('scroll', measure);
      }
      window.removeEventListener('resize', measure);
      document.removeEventListener('visibilitychange', measure);
      document.removeEventListener('focusin', measure);
      document.removeEventListener('focusout', measureSoon);
      if (resumeHandle) resumeHandle.remove();
    };
  }, []);

  return state;
}
